import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  overflow-y: auto; /* Enable scrolling within the container */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: calc(${theme.spacing.lg} + 20px);
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.xl};
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
`;

const ProgressBarContainer = styled.div`
  width: 50vw;
  max-width: 50vw;
  margin: 0 auto 40px auto;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  position: relative;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.doryGreen};
  border-radius: 2px;
  width: ${props => (props.progress / props.total) * 100}%;
  transition: width ${theme.animation.durations.fast};
`;

// Pressure Score Components
const SliderContainer = styled.div.attrs({
  className: 'slider-container'
})`
  width: 80vw !important;
  max-width: 80vw !important;
  margin: ${theme.spacing.xl} auto calc(${theme.spacing.xl} + 20px) auto;
  touch-action: pan-y;
  
  /* Override any parent constraints */
  min-width: 80vw !important;
  
  /* Ensure it's truly 80% of screen width on all devices */
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw !important;
    max-width: 80vw !important;
    min-width: 80vw !important;
  }
`;

const SliderTrack = styled.div.attrs({
  className: 'slider-track'
})`
  position: relative;
  height: 8px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 4px;
  margin: ${theme.spacing.lg} 0;
  touch-action: none;
`;

const SliderFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  border-radius: 4px;
  width: ${props => (props.value / props.maxValue) * 100}%;
  transition: width ${theme.animation.durations.normal};
`;

const SliderThumb = styled.div.attrs({
  className: 'slider-thumb'
})`
  position: absolute;
  top: 50%;
  left: ${props => (props.value / props.maxValue) * 100}%;
  transform: translate(-50%, -50%);
  width: 54px;
  height: 54px;
  background: white;
  border: 6px solid ${theme.colors.primary};
  border-radius: 50%;
  cursor: pointer;
  box-shadow: ${theme.shadows.md};
  z-index: 10;
  transition: all ${theme.animation.durations.fast};
  touch-action: none;
  user-select: none;
  
  /* Make it more obvious with a subtle inner highlight */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    opacity: 0.3;
  }
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: ${theme.shadows.lg};
  }
  
  &:active {
    transform: translate(-50%, -50%) scale(1.05);
  }
  
  /* Dragging state */
  ${props => props.isDragging && `
    transform: translate(-50%, -50%) scale(1.15);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.accent};
  `}
  
  /* Mobile touch optimization */
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 60px;
    height: 60px;
    border-width: 8px;
    
    &::after {
      width: 28px;
      height: 28px;
    }
  }
`;

const SliderLabel = styled.div`
  position: absolute;
  top: -46px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.primary};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 11;
  box-shadow: ${theme.shadows.sm};
  
  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.primary};
  }
`;

const SliderInput = styled.input`
  width: 100%;
  height: 8px;
  background: transparent;
  outline: none;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: 5;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 54px;
    height: 54px;
    background: white;
    border: 6px solid ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    opacity: 1;
    box-shadow: ${theme.shadows.md};
  }

  &::-moz-range-thumb {
    width: 54px;
    height: 54px;
    background: white;
    border: 6px solid ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    box-shadow: ${theme.shadows.md};
  }
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin: 0 0 ${theme.spacing.xl} 0;
  flex: 0 0 auto;
`;

const CurrentScore = styled.span`
  font-family: ${theme.typography.headerFont};
  font-size: 3.5rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.primary};
`;

const ScoreLabel = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.textLight};
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.sm};
`;

// Hours Worked Components
const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${theme.spacing.xl} 0;
  width: 100%;
  max-width: 300px;
`;

const HoursInput = styled.input`
  padding: ${theme.spacing.lg};
  border: 3px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.lg};
  font-family: ${theme.typography.headerFont};
  font-size: 2rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  background: white;
  text-align: center;
  width: 150px;
  transition: all ${theme.animation.durations.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px rgba(64, 92, 115, 0.1);
  }

  &:invalid {
    border-color: ${theme.colors.error};
  }
`;

const InputLabel = styled.label`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.md};
`;

const ValidationHint = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.sm};
  font-style: italic;
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-top: ${theme.spacing.sm};
  text-align: center;
`;

// Range Selector Components (for last week hours)
const RangeContainer = styled.div`
  width: 80vw;
  max-width: 500px;
  margin: ${theme.spacing.xl} auto;
`;

const RangeButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.sm};
  border: 1px solid ${props => props.selected ? theme.colors.primary : theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.selected ? theme.colors.primary : 'white'};
  color: ${props => props.selected ? 'white' : theme.colors.text};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${props => props.selected ? theme.typography.weights.semiBold : theme.typography.weights.regular};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  text-align: center;
  
  &:active {
    transform: scale(0.98);
  }
`;

// Emotional Factors Components
const FactorContainer = styled.div.attrs({
  className: 'factor-container'
})`
  width: 80vw !important;
  max-width: 80vw !important;
  margin: ${theme.spacing.xl} auto calc(${theme.spacing.xl} + 20px) auto;
  touch-action: pan-y;
  
  /* Override any parent constraints */
  min-width: 80vw !important;
  
  /* Ensure it's truly 80% of screen width on all devices */
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw !important;
    max-width: 80vw !important;
    min-width: 80vw !important;
  }
`;

const FactorLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
`;

const FactorName = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
`;

const FactorScore = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.xs};
  min-width: 80px;
  text-align: right;
`;

const FactorCurrentScore = styled.span`
  font-family: ${theme.typography.headerFont};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.primary};
`;

const FactorScoreLabel = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.textLight};
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

const PreviousButton = styled(ContinueButton)`
  /* Make the previous button much narrower */
  min-width: 60px;
  max-width: 80px;
  flex-shrink: 0;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-width: 70px;
    max-width: 90px;
    padding: 1rem 0.5rem; /* Less horizontal padding */
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-top: 0;
  margin-bottom: 0;
  width: 100%;
  flex-wrap: nowrap;
  position: relative;
  z-index: 10;
  
  /* When only one button, ensure it has side padding and doesn't go full width */
  ${ContinueButton}:only-child {
    max-width: 80%;
    min-width: 200px;
    
    /* On mobile, make it even more constrained */
    @media (max-width: ${theme.breakpoints.tablet}) {
      max-width: 75%;
      min-width: 180px;
    }
  }
`;

const JournalEntryScreen = ({ data, onJournalEntry, onNext, userProgress = {} }) => {
  // Process merge fields and format text content
  const [htmlContent, setHtmlContent] = useState('');

  // Update htmlContent whenever data.content.text changes
  useEffect(() => {
    const processedContent = formatTextContent(data.content?.text, userProgress);
    setHtmlContent(processedContent);
  }, [data.content?.text, userProgress]);

  // Multi-step state management
  const [currentStep, setCurrentStep] = useState(0);
  const [journalData, setJournalData] = useState({
    pressureScore: 5,
    hoursWorked: 12, // Start at middle position (12 hours) - for yesterday mode
    hoursWorkedRange: null, // For last week mode (e.g., '0-10', '11-20', etc.)
    emotionalStates: {
      mania: 5,        // Emotional Control
      depression: 5,   // Satisfaction
      energy: 5,       // Energy 
      focus: 5,        // Focus
      calm: 5,         // Calm
      happiness: 5,    // Positivity
      hours: 5         // Personal Identity
    }
  });
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Check if hours worked is included
  const includesHoursWorked = data.journalConfig?.collectsHours || false;
  
  // Define emotional factors
  const emotionalFactors = [
    { 
      key: 'mania', 
      name: data.journalConfig?.emotionalQuestions?.mania || 'Rate your Emotional Control', 
      lowLabel: data.journalConfig?.emotionalLabels?.mania?.low || 'Poor control', 
      highLabel: data.journalConfig?.emotionalLabels?.mania?.high || 'Great control' 
    },
    { 
      key: 'depression', 
      name: data.journalConfig?.emotionalQuestions?.depression || 'Rate your Satisfaction', 
      lowLabel: data.journalConfig?.emotionalLabels?.depression?.low || 'Unsatisfied', 
      highLabel: data.journalConfig?.emotionalLabels?.depression?.high || 'Very satisfied' 
    },
    { 
      key: 'energy', 
      name: data.journalConfig?.emotionalQuestions?.energy || 'Rate your Energy', 
      lowLabel: data.journalConfig?.emotionalLabels?.energy?.low || 'No energy', 
      highLabel: data.journalConfig?.emotionalLabels?.energy?.high || 'High energy' 
    },
    { 
      key: 'focus', 
      name: data.journalConfig?.emotionalQuestions?.focus || 'Rate your Focus', 
      lowLabel: data.journalConfig?.emotionalLabels?.focus?.low || 'Unfocused', 
      highLabel: data.journalConfig?.emotionalLabels?.focus?.high || 'Very focused' 
    },
    { 
      key: 'calm', 
      name: data.journalConfig?.emotionalQuestions?.calm || 'Rate your Calm', 
      lowLabel: data.journalConfig?.emotionalLabels?.calm?.low || 'Anxious', 
      highLabel: data.journalConfig?.emotionalLabels?.calm?.high || 'Very calm' 
    },
    { 
      key: 'happiness', 
      name: data.journalConfig?.emotionalQuestions?.happiness || 'Rate your Positivity', 
      lowLabel: data.journalConfig?.emotionalLabels?.happiness?.low || 'Negative', 
      highLabel: data.journalConfig?.emotionalLabels?.happiness?.high || 'Very positive' 
    },
    { 
      key: 'hours', 
      name: data.journalConfig?.emotionalQuestions?.hours || 'Rate your Personal Identity', 
      lowLabel: data.journalConfig?.emotionalLabels?.hours?.low || 'Lost', 
      highLabel: data.journalConfig?.emotionalLabels?.hours?.high || 'Strong identity' 
    }
  ];

  // Calculate total steps
  const totalSteps = 1 + (includesHoursWorked ? 1 : 0) + emotionalFactors.length;

  // Handle slider drag functionality
  const handleSliderDrag = (event, sliderType, factorKey = null) => {
    event.preventDefault();
    
    // Find the track element - either from the event target or by looking for the track in the DOM
    let track;
    if (event.currentTarget && event.currentTarget.classList && event.currentTarget.classList.contains('slider-track')) {
      track = event.currentTarget;
    } else {
      // For document-level events, find the track by looking for the active slider
      const activeSlider = document.querySelector(`[data-slider-type="${sliderType}"]`);
      if (activeSlider) {
        track = activeSlider.querySelector('.slider-track');
      }
    }
    
    if (!track || typeof track.getBoundingClientRect !== 'function') {
      return;
    }
    
    const rect = track.getBoundingClientRect();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    
    // Calculate position relative to track
    const relativeX = clientX - rect.left;
    const trackWidth = rect.width;
    
    let value;
    
    // Calculate value based on slider type
    if (sliderType === 'hours') {
      // Hours slider: 0-24 range with whole integers
      value = Math.round((relativeX / trackWidth) * 24);
      value = Math.max(0, Math.min(24, value));
    } else {
      // Other sliders: 0-10 range
      value = Math.round((relativeX / trackWidth) * 10);
      value = Math.max(0, Math.min(10, value));
    }
    
    // Update the appropriate state
    if (sliderType === 'pressure') {
      setJournalData(prev => ({
        ...prev,
        pressureScore: value
      }));
    } else if (sliderType === 'emotional' && factorKey) {
      setJournalData(prev => ({
        ...prev,
        emotionalStates: {
          ...prev.emotionalStates,
          [factorKey]: value
        }
      }));
    } else if (sliderType === 'hours') {
      setJournalData(prev => ({
        ...prev,
        hoursWorked: value
      }));
    }
  };

  // Handle mouse/touch events for the thumb
  const handleThumbMouseDown = (event, sliderType, factorKey = null) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
    
    const handleMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSliderDrag(e, sliderType, factorKey);
    };
    const handleMouseUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleThumbTouchStart = (event, sliderType, factorKey = null) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSliderDrag(e, sliderType, factorKey);
    };
    const handleTouchEnd = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  const validateHoursInput = (value) => {
    const num = parseFloat(value);
    
    if (value === '') {
      setError('Please enter the number of hours worked');
      return false;
    }
    
    if (isNaN(num)) {
      setError('Please enter a valid number');
      return false;
    }
    
    if (num < 0) {
      setError('Hours cannot be negative');
      return false;
    }
    
    if (num > 24) {
      setError('Hours cannot exceed 24 in a day');
      return false;
    }
    
    setError('');
    return true;
  };

  // Helper function to get middle value of a range and convert to daily hours
  const getDailyHoursFromRange = (range) => {
    if (!range) return null;
    
    if (range === '120+') {
      // Use 130 as middle estimate for 120+ range
      return Math.round(130 / 7);
    }
    
    const [min, max] = range.split('-').map(Number);
    const middle = (min + max) / 2;
    return Math.round(middle / 7); // Convert weekly hours to daily hours and round to nearest integer
  };

  const handleContinue = () => {
    setError('');

    // Step 0: Pressure Score
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }

    // Step 1: Hours Worked (if included)
    if (includesHoursWorked && currentStep === 1) {
      const hoursWorkedType = data.journalConfig?.hoursWorkedType || 'yesterday';
      
      if (hoursWorkedType === 'lastWeek') {
        // Validate that a range is selected
        if (!journalData.hoursWorkedRange) {
          setError('Please select a range for hours worked last week');
          return;
        }
      }
      // For yesterday mode, slider always has a valid value, no validation needed
      setCurrentStep(2);
      return;
    }

    // Emotional factors steps
    const emotionalStepStart = includesHoursWorked ? 2 : 1;
    const emotionalStepIndex = currentStep - emotionalStepStart;
    
    if (emotionalStepIndex < emotionalFactors.length - 1) {
      setCurrentStep(currentStep + 1);
      return;
    }

    // Final step - save journal entry and navigate
    handleSubmitJournal();
  };

  const handleSubmitJournal = () => {
    const hoursWorkedType = data.journalConfig?.hoursWorkedType || 'yesterday';
    let hoursWorked = null;
    
    if (includesHoursWorked) {
      if (hoursWorkedType === 'lastWeek') {
        // Convert weekly range to daily hours (already rounded in getDailyHoursFromRange)
        hoursWorked = getDailyHoursFromRange(journalData.hoursWorkedRange);
      } else {
        // Yesterday mode - use the slider value directly, round to nearest integer
        hoursWorked = Math.round(parseFloat(journalData.hoursWorked) || 0);
      }
    }
    
    const finalJournalData = {
      type: 'journal_entry',
      pressureScore: journalData.pressureScore,
      ...(includesHoursWorked && hoursWorked !== null && { hoursWorked: hoursWorked }),
      emotionalStates: journalData.emotionalStates,
      timestamp: Date.now(),
      screenKey: data.key
    };

    // onJournalEntry already navigates via FunnelContainer.handleNext — do not also call onNext
    // or conversions / progress updates fire twice.
    if (onJournalEntry) {
      onJournalEntry(finalJournalData);
    } else {
      onNext?.(data.key);
    }
  };

  const renderStep = () => {
    // Step 0: Pressure Score
    if (currentStep === 0) {
      return (
        <>
          <Title>{data.journalConfig?.pressureQuestion || 'How would you rate the current level of pressure you\'re under?'}</Title>
          
          <SliderContainer data-slider-type="pressure">
            <SliderTrack 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSliderDrag(e, 'pressure');
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSliderDrag(e, 'pressure');
              }}
            >
              <SliderFill value={journalData.pressureScore} maxValue={10} />
              <SliderThumb 
                value={journalData.pressureScore} 
                maxValue={10}
                isDragging={isDragging}
                onMouseDown={(e) => handleThumbMouseDown(e, 'pressure')}
                onTouchStart={(e) => handleThumbTouchStart(e, 'pressure')}
              >
                <SliderLabel>{journalData.pressureScore}</SliderLabel>
              </SliderThumb>
              <SliderInput
                type="range"
                min="0"
                max="10"
                value={journalData.pressureScore}
                onChange={(e) => setJournalData(prev => ({
                  ...prev,
                  pressureScore: parseInt(e.target.value)
                }))}
              />
            </SliderTrack>
            
            <ScaleLabels>
              <span>{data.journalConfig?.pressureLabels?.low || 'No pressure'}</span>
              <span>{data.journalConfig?.pressureLabels?.high || 'Extreme pressure'}</span>
            </ScaleLabels>
          </SliderContainer>

        </>
      );
    }

    // Step 1: Hours Worked (if included)
    if (includesHoursWorked && currentStep === 1) {
      const hoursWorkedType = data.journalConfig?.hoursWorkedType || 'yesterday';
      
      // Range options for last week (largest ranges first)
      const weekRanges = [
        { label: '120+ hours', value: '120+' },
        { label: '111-120 hours', value: '111-120' },
        { label: '101-110 hours', value: '101-110' },
        { label: '91-100 hours', value: '91-100' },
        { label: '81-90 hours', value: '81-90' },
        { label: '71-80 hours', value: '71-80' },
        { label: '61-70 hours', value: '61-70' },
        { label: '51-60 hours', value: '51-60' },
        { label: '41-50 hours', value: '41-50' },
        { label: '31-40 hours', value: '31-40' },
        { label: '21-30 hours', value: '21-30' },
        { label: '11-20 hours', value: '11-20' },
        { label: '0-10 hours', value: '0-10' }
      ];
      
      if (hoursWorkedType === 'lastWeek') {
        return (
          <>
            <Title>{data.journalConfig?.hoursQuestion || 'How many hours did you work last week?'}</Title>
            
            <RangeContainer>
              {weekRanges.map((range) => (
                <RangeButton
                  key={range.value}
                  selected={journalData.hoursWorkedRange === range.value}
                  onClick={() => {
                    // Set the selected range
                    setJournalData(prev => ({
                      ...prev,
                      hoursWorkedRange: range.value
                    }));
                    // Advance to next step (emotional factors) after half second delay
                    setTimeout(() => {
                      setCurrentStep(2);
                    }, 500);
                  }}
                >
                  {range.label}
                </RangeButton>
              ))}
            </RangeContainer>
          </>
        );
      } else {
        // Yesterday mode (default) - show slider
        return (
          <>
            <Title>{data.journalConfig?.hoursQuestion || 'How many hours did you work on your most recent full work day?'}</Title>
            
            <SliderContainer data-slider-type="hours">
              <SliderTrack 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSliderDrag(e, 'hours');
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSliderDrag(e, 'hours');
                }}
              >
                <SliderFill value={journalData.hoursWorked || 0} maxValue={24} />
                <SliderThumb 
                  value={journalData.hoursWorked || 0} 
                  maxValue={24}
                  isDragging={isDragging}
                  onMouseDown={(e) => handleThumbMouseDown(e, 'hours')}
                  onTouchStart={(e) => handleThumbTouchStart(e, 'hours')}
                >
                  <SliderLabel>{journalData.hoursWorked || 0}</SliderLabel>
                </SliderThumb>
                <SliderInput
                  type="range"
                  min="0"
                  max="24"
                  step="1"
                  value={journalData.hoursWorked || 0}
                  onChange={(e) => setJournalData(prev => ({
                    ...prev,
                    hoursWorked: parseInt(e.target.value)
                  }))}
                />
              </SliderTrack>
              
              <ScaleLabels>
                <span>{data.journalConfig?.hoursLabels?.low || '0 hours'}</span>
                <span>{data.journalConfig?.hoursLabels?.high || '24 hours'}</span>
              </ScaleLabels>
            </SliderContainer>

          </>
        );
      }
    }

    // Emotional factors steps
    const emotionalStepStart = includesHoursWorked ? 2 : 1;
    const emotionalStepIndex = currentStep - emotionalStepStart;
    const currentFactor = emotionalFactors[emotionalStepIndex];

    if (currentFactor) {
      return (
        <>
          <Title>{currentFactor.name}</Title>
          
          <FactorContainer data-slider-type="emotional">
            <SliderTrack 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSliderDrag(e, 'emotional', currentFactor.key);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSliderDrag(e, 'emotional', currentFactor.key);
              }}
            >
              <SliderFill value={journalData.emotionalStates[currentFactor.key]} maxValue={10} />
              <SliderThumb 
                value={journalData.emotionalStates[currentFactor.key]} 
                maxValue={10}
                isDragging={isDragging}
                onMouseDown={(e) => handleThumbMouseDown(e, 'emotional', currentFactor.key)}
                onTouchStart={(e) => handleThumbTouchStart(e, 'emotional', currentFactor.key)}
              >
                <SliderLabel>{journalData.emotionalStates[currentFactor.key]}</SliderLabel>
              </SliderThumb>
              <SliderInput
                type="range"
                min="0"
                max="10"
                value={journalData.emotionalStates[currentFactor.key]}
                onChange={(e) => setJournalData(prev => ({
                  ...prev,
                  emotionalStates: {
                    ...prev.emotionalStates,
                    [currentFactor.key]: parseInt(e.target.value)
                  }
                }))}
              />
            </SliderTrack>
            
            <ScaleLabels>
              <span>{currentFactor.lowLabel}</span>
              <span>{currentFactor.highLabel}</span>
            </ScaleLabels>
          </FactorContainer>

        </>
      );
    }

    return null;
  };

  const getStepText = () => {
    if (currentStep === totalSteps - 1) {
      // Use admin-configured CTA text for the final step, fallback to default
      return data.content?.ctaText || "Next";
    }
    return "Next";
  };

  const canContinue = () => {
    if (includesHoursWorked && currentStep === 1) {
      return journalData.hoursWorked !== '' && !error;
    }
    return true;
  };

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        <ProgressBarContainer>
          <ProgressFill progress={currentStep + 1} total={totalSteps} />
        </ProgressBarContainer>

        {renderStep()}
        
        {/* CTA button appears right after content */}
        <ButtonGroup>
          {currentStep > 0 && (
            <PreviousButton 
              onClick={() => setCurrentStep(currentStep - 1)}
              style={{ background: theme.colors.secondary, color: theme.colors.text }}
            >
              &lt;&lt;
            </PreviousButton>
          )}
          
          <ContinueButton 
            onClick={handleContinue}
            disabled={!canContinue()}
          >
            {getStepText()}
          </ContinueButton>
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default JournalEntryScreen; 